import type { Tower } from "../entities/Tower.ts";
import type { Enemy } from "../entities/Enemy.ts";
import type { Element } from "../entities/types.ts";
import type { ActiveSynergy } from "../entities/types.ts";
import type { CellBlockedPredicate } from "../core/LineOfSight.ts";
import { hasLineOfSight } from "../core/LineOfSight.ts";
import type { DraftSystem } from "./DraftSystem.ts";
import { updateBeamTower, updateSignalTower } from "./BeamCombat.ts";
import type { BeamCombatContext } from "./BeamCombat.ts";
import { fireLobProjectile, fireProjectile } from "./ProjectileCombat.ts";
import type { ProjectileCombatContext } from "./ProjectileCombat.ts";
import { findClusterTarget, findTarget } from "./Targeting.ts";
import { updateSniperTower } from "./SniperCombat.ts";
import { virusInfectionProfile } from "./VirusLevels.ts";
import { isStealthHidden } from "./StealthGuard.ts";

/** Per-frame context handed to every tower behavior. Bundles the two existing
 *  combat contexts plus the loop-local values the generic projectile branch reads.
 *  Built once per frame in CombatSystem.updateTowers (values are stable within a frame). */
export interface TowerUpdateContext {
  beam: BeamCombatContext;
  projectile: ProjectileCombatContext;
  enemies: Enemy[];
  rangeMultiplier: number;
  targetFastest: boolean;
  draftSystem: DraftSystem | null;
  losBlockedAt: CellBlockedPredicate | null;
  activeSynergies: readonly ActiveSynergy[];
}

export type TowerBehavior = (
  tower: Tower,
  dt: number,
  ctx: TowerUpdateContext,
) => void;

/** Generic projectile tower (fire/water): cooldown → acquire target → fire.
 *  Extracted verbatim from CombatSystem.updateTowers' former else-branch. */
export function updateProjectileTower(
  tower: Tower,
  dt: number,
  ctx: TowerUpdateContext,
): void {
  const epicFireRate = ctx.draftSystem?.getGlobalFireRateMultiplier() ?? 1;
  tower.updateCooldown(dt * epicFireRate);

  const effectiveRange = tower.range * ctx.rangeMultiplier;
  const losClear = (enemy: Enemy): boolean =>
    ctx.losBlockedAt == null
      ? true
      : hasLineOfSight(tower.x, tower.y, enemy.x, enemy.y, ctx.losBlockedAt);
  tower.target = findTarget(
    tower,
    ctx.enemies,
    effectiveRange,
    false,
    ctx.targetFastest,
    losClear,
  );

  if (tower.target && tower.canFire()) {
    fireProjectile(tower, ctx.projectile);
    tower.fire();
  }

  // BL-175 / D-266 — water's GOLD "ABSOLUTE ZERO · Cryo-Nova" cadence. Inert below
  // GOLD / for fire (no `goldNovaPeriodMs`), so byte-identical for every fixture.
  updateCryoNova(tower, dt, ctx);
}

/** BL-175 / D-266 (S170) — water's GOLD signature "ABSOLUTE ZERO · Cryo-Nova". On
 *  its cadence (`goldNovaPeriodMs`, banked in `tower.goldTimer`) the GOLD water
 *  tower emits a board-region STASIS pulse that freezes every revealed foe within
 *  `goldNovaRadius` px solid (slow ×0) for `goldNovaFreezeMs`. Brand-new area
 *  stasis (NOT the chain). Wholly deterministic: a dt timer + a radius scan, no
 *  `rng()`. Gated on `tower.isGold()` + the data, so it never ticks below GOLD
 *  (the `goldTimer` stays 0) → no surface in any fixture, byte-identical. */
function updateCryoNova(tower: Tower, dt: number, ctx: TowerUpdateContext): void {
  const s = tower.special;
  if (
    !tower.isGold() ||
    !s ||
    s.goldNovaPeriodMs === undefined ||
    s.goldNovaRadius === undefined ||
    s.goldNovaFreezeMs === undefined
  ) {
    return;
  }
  tower.goldTimer += dt;
  if (tower.goldTimer < s.goldNovaPeriodMs) return;
  tower.goldTimer -= s.goldNovaPeriodMs;
  tower.goldPulseT = 320; // render-only apex flash

  const rSq = s.goldNovaRadius * s.goldNovaRadius;
  for (const enemy of ctx.enemies) {
    if (!enemy.active) continue;
    if (isStealthHidden(enemy)) continue;
    const dx = enemy.x - tower.x;
    const dy = enemy.y - tower.y;
    if (dx * dx + dy * dy > rSq) continue;
    enemy.applySlow(0, s.goldNovaFreezeMs);
  }
  ctx.projectile.bus.emit("tower.fired", { tower, projectile: null });
}

/**
 * BL-117 cover ruling (D-231, Variant A): line-of-sight cover is asymmetric by
 * archetype. **Direct-fire** towers respect cover — fire/water projectiles,
 * lightning/signal beams, and the kinetic rail all gate acquisition on
 * `hasLineOfSight` (a blocker between tower and target protects the target).
 * **The virus Contagion-Node is the deliberate exception:** it lobs an arcing
 * projectile at a ground cluster point (`findClusterTarget`, which takes NO LoS
 * predicate), so it fires *over* blockers. That is its tactical identity and the
 * intended counter to enemies turtling behind cover — do not add a LoS gate here.
 */
function updateVirusTower(
  tower: Tower,
  dt: number,
  ctx: TowerUpdateContext,
): void {
  const epicFireRate = ctx.draftSystem?.getGlobalFireRateMultiplier() ?? 1;
  tower.updateCooldown(dt * epicFireRate);

  // PT-01: findClusterTarget is a pure O(n^2) scan whose result is ONLY consumed
  // when firing. Gating it on canFire() skips the scan on every cooldown frame
  // (~9 of every 10 frames at virus fireRate 6). Behaviour-identical: the aim
  // point is never stored on the tower nor used for rendering. Measured ~55x
  // fewer scans in late-wave crowds. See D-122.
  if (!tower.canFire()) return;

  const effectiveRange = tower.range * ctx.rangeMultiplier;
  const zoneRadius = virusInfectionProfile(tower.level).zoneRadius;
  const aim = findClusterTarget(tower, ctx.enemies, effectiveRange, zoneRadius);

  if (aim) {
    fireLobProjectile(tower, aim.x, aim.y, ctx.projectile);
    tower.fire();
  }
}

/** Element → behavior. `satisfies` makes this exhaustive: adding an element to
 *  ELEMENTS without a behavior here is a COMPILE ERROR. That is the point. */
export const TOWER_BEHAVIORS = {
  fire: updateProjectileTower,
  water: updateProjectileTower,
  lightning: (tower, dt, ctx) => updateBeamTower(tower, dt, ctx.beam),
  signal: (tower, dt, ctx) => updateSignalTower(tower, dt, ctx.beam),
  kinetic: (tower, dt, ctx) => updateSniperTower(tower, dt, ctx),
  virus: (tower, dt, ctx) => updateVirusTower(tower, dt, ctx),
} satisfies Record<Element, TowerBehavior>;
